import { Box, Typography } from "@mui/material";
import {
  NotificationPreferenceScope,
  NotificationPreferenceSource,
} from "generated-graphql/graphql";

import {
  AlertRow,
  PREFERENCE_GRID_COLUMNS,
  PREFERENCE_GRID_MIN_WIDTH,
  type AlertRowData,
} from "./AlertRow";
import { OverridesMarker } from "./OverridesMarker";
import { CHANNEL_CELL_SX, channelLabel, SECTION_OFF_LABEL } from "./preferenceProvenance";
import { SectionAccordion, SectionHeaderStatus } from "./SectionAccordion";
import { SectionRollupRow } from "./SectionRollupRow";
import {
  countDeliveringRows,
  type DeliverySection,
  type EmailCadenceChoice,
  PREFERENCE_ROW_CHANNELS,
  type DraftParentSwitch,
  type NotificationPreferenceDraft,
} from "./useNotificationPreferenceDraft";

/**
 * One section of the preferences page: a heading, a {@link SectionRollupRow} for the
 * whole category, and the notification rows beneath it.
 *
 * The heading says "Off" when nothing in the section will fire and says nothing
 * otherwise. It can disagree with the switches inside, because a page-wide channel
 * mute silences them without moving them.
 */

export type AlertSectionData = {
  groupId: string;
  label: string;
  /** One per channel, in the same order as the columns they sit in. */
  parentSwitches: readonly (DraftParentSwitch & { source: NotificationPreferenceSource })[];
  rows: readonly AlertRowData[];
};

export type AlertSectionAccordionProps = {
  section: AlertSectionData;
  channelParentSwitches: readonly (DraftParentSwitch & { source: NotificationPreferenceSource })[];
  draft: NotificationPreferenceDraft;
  scope: NotificationPreferenceScope;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** True while a write is in flight; see {@link PreferenceSwitch}. */
  disabled: boolean;
  /** Toggles whose own save is still in flight; each is disabled until it lands. */
  pendingKeys?: ReadonlySet<string>;
  onToggle: (kind: string, channel: string, enabled: boolean) => void;
  onEmailChoice: (kind: string, choice: EmailCadenceChoice) => void;
  setSectionChannel: (section: DeliverySection, channel: string, enabled: boolean) => void;
  /** Marks rows whose saved setting is the reader's own; see the Overridden chip in AlertRow. */
  showOverrides?: boolean;
};

/**
 * The row of column headings.
 *
 * The channel columns are generated from {@link PREFERENCE_ROW_CHANNELS} rather
 * than written out, so a heading can never end up over the column it does not name.
 *
 * It carries no "muted from above" cue of its own. One badge per column restated
 * what the greyscale and each switch's own description already say, and at three
 * sections deep the page was mostly badges.
 */
function ColumnHeaders() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: PREFERENCE_GRID_COLUMNS,
        alignItems: "flex-end",
        columnGap: 1,
        // Tight both ways: close under the section header it belongs to, and close
        // to the rule beneath, so the labels read as part of the first line rather
        // than as their own band.
        pt: 0.5,
        pb: 0.25,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="overline" color="text.secondary">
        Notification
      </Typography>

      {PREFERENCE_ROW_CHANNELS.map((channel) => (
        <Box key={channel} sx={CHANNEL_CELL_SX}>
          <Typography variant="overline" color="text.secondary">
            {channelLabel(channel)}
          </Typography>
        </Box>
      ))}

      {/* Caret lane, empty here. */}
      <Box />
    </Box>
  );
}

/** Whether anything in the section, its own switches included, was set here. */
function sectionHasOverrides(section: AlertSectionData): boolean {
  const own = (source: NotificationPreferenceSource) =>
    source === NotificationPreferenceSource.User;
  return (
    section.parentSwitches.some((parent) => own(parent.source)) ||
    section.rows.some((row) => row.preferences.some((preference) => own(preference.source)))
  );
}

export function AlertSectionAccordion({
  section,
  channelParentSwitches,
  draft,
  scope,
  expanded,
  onToggleExpanded,
  disabled,
  pendingKeys,
  onToggle,
  onEmailChoice,
  setSectionChannel,
  showOverrides = false,
}: AlertSectionAccordionProps) {
  const deliveringCount = countDeliveringRows(section, draft, channelParentSwitches);

  return (
    <SectionAccordion
      label={section.label}
      status={
        <>
          {deliveringCount === 0 && <SectionHeaderStatus>{SECTION_OFF_LABEL}</SectionHeaderStatus>}
          {showOverrides && sectionHasOverrides(section) && (
            <OverridesMarker title="Some settings in this section differ from the default setting" />
          )}
        </>
      }
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      gridTemplateColumns={PREFERENCE_GRID_COLUMNS}
      controlColumns={PREFERENCE_ROW_CHANNELS.length}
    >
      {/*
          The grid holds `PREFERENCE_GRID_MIN_WIDTH` of columns at their floor —
          restated as a reference rather than a number, because the last hard-coded
          figure went stale the moment a column gained a minimum. A narrow window
          scrolls it rather than crushing the alert names into a column of single
          characters. The scroll lives here rather than on the page so the Delivery
          card and the section headers stay put while the grid moves.
        */}
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: PREFERENCE_GRID_MIN_WIDTH }}>
          <ColumnHeaders />
          <SectionRollupRow
            section={section}
            channelParentSwitches={channelParentSwitches}
            draft={draft}
            scope={scope}
            disabled={disabled}
            pendingKeys={pendingKeys}
            setSectionChannel={setSectionChannel}
          />
          {/*
              A mute above marks each row's toggle columns rather than the whole
              block: the titles and descriptions stay legible, because a reader
              deciding whether to lift it has to be able to read what is under it.
              `AlertRow` applies that per channel, and nothing wraps it in a second
              layer — two nested treatments would compound into unreadable.
            */}
          {section.rows.map((row) => (
            <AlertRow
              key={row.kind}
              row={row}
              sectionParentSwitches={section.parentSwitches}
              channelParentSwitches={channelParentSwitches}
              draft={draft}
              scope={scope}
              disabled={disabled}
              pendingKeys={pendingKeys}
              onToggle={onToggle}
              onEmailChoice={onEmailChoice}
              showOverrides={showOverrides}
            />
          ))}
        </Box>
      </Box>
    </SectionAccordion>
  );
}
